import { CalendarDays } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { EmptyState } from '@/components/common/States'
import { Avatar } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'
import { fmtDate } from '../../../shared/format'
import { isOverdue } from '../../taskFilters'
import type { Task } from '../../types'
import { useStaffLookup } from '../../ui'
import { TaskStatusBadge } from '../StatusBadges'

/** Görevlerin sıkı listesi; her satır görev detayına gider. */
export function TaskMiniList({ projectId, tasks, emptyText }: { projectId: string; tasks: Task[]; emptyText?: string }) {
  const { t } = useTranslation()
  const { nameOf } = useStaffLookup()

  if (tasks.length === 0) {
    return (
      <div className="rounded-xl border">
        <EmptyState title={emptyText ?? t('projects.tasks.empty')} />
      </div>
    )
  }

  return (
    <ul className="divide-y overflow-hidden rounded-xl border">
      {tasks.map((task) => (
        <li key={task.id}>
          <Link
            to={`/projects/${projectId}/tasks/${task.id}`}
            className="flex items-center gap-3 px-3 py-2.5 transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none"
          >
            <span className="w-16 shrink-0 font-mono text-xs text-primary">{task.key}</span>
            <span className="min-w-0 flex-1 truncate text-sm font-medium">{task.title}</span>
            <span className="hidden sm:inline-flex">
              <TaskStatusBadge status={task.status} />
            </span>
            <span className={cn('hidden w-24 shrink-0 items-center gap-1 text-xs tabular-nums text-muted-foreground md:inline-flex', isOverdue(task) && 'font-medium text-destructive')}>
              <CalendarDays className="size-3.5" aria-hidden />
              {fmtDate(task.dueDate)}
            </span>
            {task.assigneeId ? <Avatar name={nameOf(task.assigneeId)} className="size-7 [&>span]:text-[10px]" /> : <span className="size-7 shrink-0" />}
          </Link>
        </li>
      ))}
    </ul>
  )
}
